import React, { HTMLAttributes, useEffect, useMemo, useRef, useState } from 'react';
import request from '../lib/request';
import { useAsync, url as urlKit } from 'eastedu-kit';
import { css, cx } from 'emotion';
import { decode } from 'js-base64';
import env_api from '../config/api';
import trimEnd from 'lodash/trimEnd';

const PREVIEW_ENGINE_MESSAGE_TYPE = 'eurooffice-viewer:preview-engine';

type PreviewEngine = 'office-online' | 'eurooffice';

interface PreviewEngineMessage {
  type: typeof PREVIEW_ENGINE_MESSAGE_TYPE;
  engine: PreviewEngine;
}

interface PreviewEngineState {
  src: string;
  engine: PreviewEngine;
}

interface PreviewEngineController {
  iframeRef: React.RefObject<HTMLIFrameElement>;
  previewEngine: PreviewEngine | null;
}

const styles = {
  wrapper: css`
    position: relative;
    height: 100%;
    width: 100%;
  `,
  iframe: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  `,
  fullscreen_mask: css`
    position: absolute;
    right: 6px;
    background-color: #fff;
    width: 36px;
    height: 20px;
    z-index: 10;
    bottom: 1px;

    &.ppt,
    &.pptx {
      background-color: #444;
    }
  `,
};

interface OfficeViewerProps {
  viewType?: 'embed' | 'normal';
  url: string;
  iframeProps?: HTMLAttributes<HTMLDivElement>;

  style?: React.CSSProperties;
  className?: string;
  /** 传递扩展名将开启全屏按钮屏蔽 */
  extension?: string;
}

/** 判断消息是否为预览站点发送的引擎通知。 */
function isPreviewEngineMessage(data: unknown): data is PreviewEngineMessage {
  if (!data || typeof data !== 'object') return false;

  const message = data as Partial<PreviewEngineMessage>;
  return (
    message.type === PREVIEW_ENGINE_MESSAGE_TYPE &&
    (message.engine === 'office-online' || message.engine === 'eurooffice')
  );
}

// 从旧版的office预览器的url中提取文件地址进行预览
function parseFileSrcFromOfficePreviewUrl(url: string) {
  try {
    const wopiSrc = new URL(url).searchParams.get('WOPISrc');
    if (!wopiSrc) {
      throw new Error('缺少参数 WOPISrc');
    }
    //example: https://wopi.eastedu.com/api/wopi/files/aHR0cHM6Ly90ZXN0LTEyNTQ0NTU4NjEuZWFzdGVkdS5jb20vZGF0YS9kc2gtYXBpLzIwMjYwOTA5LzMxNzIzODI4OTc5ZDQxNmM3YTI0ODI5NzlhOTdkMmEzLmRvY3hc
    const srcBase64 = wopiSrc.slice(wopiSrc.lastIndexOf('/') + 1);
    return trimEnd(decode(srcBase64), '\\'); // 解析出来的地址结尾多一个反斜杠需要移除
  } catch (err) {
    console.error('parseFileSrcFromOfficePreviewUrl', err);
  }
  return null;
}

/** 监听预览 iframe 的引擎消息，并返回与当前地址匹配的引擎状态。 */
function usePreviewEngine(
  src: string | null,
  officePreviewUrl: string | undefined,
): PreviewEngineController {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [previewEngineState, setPreviewEngineState] = useState<PreviewEngineState | null>(null);
  const previewEngine =
    previewEngineState && previewEngineState.src === src ? previewEngineState.engine : null;
  const officeViewerOrigin = useMemo(() => {
    try {
      return new URL(env_api.officeViewerService).origin;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!src || src === officePreviewUrl || !officeViewerOrigin) return;

    const handlePreviewMessage = (event: MessageEvent) => {
      const iframeWindow = iframeRef.current && iframeRef.current.contentWindow;
      if (!iframeWindow || event.source !== iframeWindow || event.origin !== officeViewerOrigin)
        return;
      if (!isPreviewEngineMessage(event.data)) return;

      setPreviewEngineState({ src, engine: event.data.engine });
    };

    window.addEventListener('message', handlePreviewMessage);
    return () => window.removeEventListener('message', handlePreviewMessage);
  }, [src, officePreviewUrl, officeViewerOrigin]);

  return { iframeRef, previewEngine };
}

function OfficeViewer(props: OfficeViewerProps) {
  const { viewType = 'embed', url, style, className, iframeProps, extension } = props;

  const officePreviewResult = useAsync(async () => {
    if (/office\.eastedu\.com/.test(url)) {
      return url;
    }
    const urlMap = {
      embed: 'https://wopi.eastedu.com/api/wopi/link/embed-view',
      normal: 'https://wopi.eastedu.com/api/wopi/link/view',
    };

    const res = await request(`${urlMap[viewType]}/${encodeURIComponent(url)}`);
    return res.data;
  }, [viewType, url]);

  const officePreviewUrl = officePreviewResult.result; // office预览地址，解析出文件src供新预览器使用，解析失败时兜底

  const src = useMemo(() => {
    if (!officePreviewUrl) return null;

    const parsedSrc = parseFileSrcFromOfficePreviewUrl(officePreviewUrl);
    // 解析失败时直接降级用旧预览地址，保持旧版行为
    if (!parsedSrc) return officePreviewUrl;

    // 详细对接文档查看: https://docs.eastedu.ltd/gitlab/infrastructure-frontend/eurooffice-viewer
    return urlKit.appendQuery(`${env_api.officeViewerService}/preview`, {
      src: parsedSrc,
      previewUrl: officePreviewUrl,
    });
  }, [officePreviewUrl]);

  const { iframeRef, previewEngine } = usePreviewEngine(src, officePreviewUrl);
  const shouldShowFullscreenMask = Boolean(extension) && previewEngine !== 'eurooffice';

  return (() => {
    if (!officePreviewResult.result || officePreviewResult.loading) {
      return null;
    }
    if (officePreviewResult.error) {
      return (
        <span
          style={{
            color: 'red',
          }}
        >
          预览出错，请稍后重试。
        </span>
      );
    }
    return (
      <div className={cx(styles.wrapper, className)} style={style}>
        {!!src && <iframe src={src} className={styles.iframe} {...iframeProps} ref={iframeRef} />}
        {/* Office Online 模式下显示遮罩，确认切换到 EuroOffice 后隐藏 */}
        {shouldShowFullscreenMask && <div className={cx(styles.fullscreen_mask, extension)} />}
      </div>
    );
  })();
}

export default OfficeViewer;
